import { useEffect, useRef, useState } from "react";
import { formatCV } from "@util/string";
import { CVData } from "@type/cv";
import { getApplicantCV } from "@service/cv";
import { catchErrorLog } from "@util/sentry";
import { getFirstName } from "@util/name";
import { VideoInterviewRef } from "@type/videoInterview";
import { DailyProvider, useCallObject } from "@daily-co/daily-react";
import { DailyCall } from "@daily-co/daily-js";
import { SpeechDetectionProvider } from "@context/speech_detection_context";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "@component/card";
import { ArrowRightIcon, CheckIcon, Link2, Link2OffIcon, VideoIcon } from "lucide-react";
import { Button } from "@component/button";
import { getInterviewStatus, updateInterviewStatusV2 } from "@service/interview";
import { interviewStatusAborted, interviewStatusPartial, interviewStatusPendingInterview } from "@constant/app";
import { trackEvent } from "@util/mixpanel";
import { useQuery } from "@util/useQuery";
import { applicantRequestNewAssessmentLink, getJobDetailGuest, updateApplicantStatusAssessmentCompletion } from "@service/job";
import { Job } from "@type/job";
import { setCookie } from "@util/cookie";
import BasicLayout from "@container/layout/basic";
import InterviewShortCard from "@container/interview-short-card";
import VoiceInterview from "@container/voice-interview-test";
import SocialProofIndicator from "@container/social-proof-indicator";

export default function GeneralInterviewV2() {
    const queryParams = useQuery();
    const [cvData, setCVData] = useState<CVData | null>(null);
    const [interviewStage, setInterviewStage] = useState<string>("interview");
    const [userId, setUserId] = useState<string>("");
    const [currentInterviewStatus, setCurrentInterviewStatus] = useState<string>("");
    const [jobDetail, setJobDetail] = useState<Job | null>(null);
    const [isLoading, setLoading] = useState<boolean>(true);
    const [isRequestingNewLink, setIsRequestingNewLink] = useState<boolean>(false);
    const [isRequestedNewLink, setIsRequestedNewLink] = useState<boolean>(false);
    const [isLoadingInterviewStatus, setIsLoadingInterviewStatus] = useState<boolean>(true);
    const [interviewStatus, setInterviewStatus] = useState<string>("");

    const videoInterviewRef = useRef<VideoInterviewRef>(null);
    const callObject = useCallObject({}) as DailyCall;
    const payloadToken = queryParams.get("token") ? JSON.parse(atob(queryParams.get("token") || "")) : null;

    const getCandidateName = () => {
        if (!cvData) return "Anonymous Candidate";
        const cv = cvData
        return cv?.name || "Anonymous Candidate";
    };

    const INTERVIEW_CONFIG = {
        conversationName: `${getCandidateName()}: Screening Interview (${new Date().toISOString()})`,
        conversationalContext: `Never ask the user about a 'position', this is a general screening interview. \n\nCandidate CV/Resume:\n${formatCV(cvData)}`,
    };

    const REPLICA_CONFIG = {
        label: import.meta.env.VITE_TAVUS_REPLICA_LABEL,
        replicaId: import.meta.env.VITE_TAVUS_REPLICA_ID,
        personaId: import.meta.env.VITE_TAVUS_PERSONA_ID,
        greeting: import.meta.env.VITE_TAVUS_GREETING,
    };

    useEffect(() => {
        if (!queryParams.get("token")) {
            setLoading(false);
            return;
        }

        getJobDetailGuest(payloadToken.job_id).then((resp) => {
            if (resp) {
                setJobDetail(resp);
            }
        }).catch((error) => {
            catchErrorLog(`Error fetching job details for interview: ${error}`);
        }).finally(() => {
            setLoading(false);
        })

        getApplicantCV(payloadToken.user_id)
            .then((res) => {
                if (res.status === 200) {
                    setUserId(payloadToken.user_id);
                    setCVData(res.data.data.CV_data);
                }
            })
            .catch((err) => {
                catchErrorLog(`Error fetching CV data: ${err}`);
            });

        getInterviewStatus(null).then((resp) => {
            setInterviewStatus(resp?.data?.status || null);
        }).finally(() => {
            setIsLoadingInterviewStatus(false);
        });

        if (payloadToken && payloadToken.access_token) {
            setCookie("tempAuthToken", payloadToken.access_token);
        }
    }, [queryParams.get("token")])

    // Handle completion of interview and cleanup
    const handleInterviewComplete = () => {
        // End the interview if it's still active
        if (videoInterviewRef.current) {
            console.log("Ending interview and cleaning up media resources");
            videoInterviewRef.current.endInterview('interview_completed');
        }

        // Update state to show completion UI for regular interviews
        setInterviewStage("complete");
    };

    const onReload = () => {
        updateInterviewStatusV2({
            "new_status": interviewStatusPendingInterview,
            "retake_reason": "Interview too short"
        }).then((res) => {
            console.log("Updated status from retake", res);
            trackEvent("interview_retake", {
                "reason": "Interview too short"
            })
            window.location.reload();
        });
    }

    const onInterviewEnded = () => {
        updateApplicantStatusAssessmentCompletion(
            payloadToken.job_id,
            payloadToken.user_id,
            currentInterviewStatus
        ).then((resp) => {
            console.log("Applicant assessment completion updated", resp);
        });
    }

    const onRequestNewLink = () => {
        setIsRequestingNewLink(true);
        applicantRequestNewAssessmentLink(payloadToken.job_id, payloadToken.user_id).then((resp) => {
            if (resp.status === 200) {
                setIsRequestedNewLink(true);
            }
        }).finally(() => {
            setIsRequestingNewLink(false);
        });
    }

    return (
        <BasicLayout>
            <div className="container max-w-6xl mx-auto px-4 sm:py-8 flex flex-col min-h-[510px] items-center justify-center">
                {isLoadingInterviewStatus ? (
                    <div className="container mx-auto px-0 sm:px-4 pt-0 pb-2 sm:py-8 max-w-xl">
                        <div
                            className="bg-white dark:bg-gray-900 rounded-3xl p-8 shadow-2xl text-center w-full mx-auto"
                            style={{
                                boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
                            }}
                        >
                            <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-blue-100 text-blue-700 mx-auto mb-6">
                                <VideoIcon className="h-6 sm:h-10 w-6 sm:w-10" />
                            </div>
                            <h1 className="text-3xl font-bold mb-2">
                                Screening Interview
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400 mb-8 mx-auto whitespace-pre-line">
                                Please wait while we prepare your interview session...
                            </p>
                        </div>
                    </div>
                ) : (!isLoadingInterviewStatus && interviewStatus !== "Pending Interview") ? (
                    <div className="container mx-auto px-0 sm:px-4 pt-0 pb-2 sm:py-8 max-w-xl">
                        <div
                            className="bg-white dark:bg-gray-900 rounded-3xl p-8 shadow-2xl text-center w-full mx-auto"
                            style={{
                                boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
                            }}
                        >
                            <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-blue-100 text-blue-700 mx-auto mb-6">
                                <VideoIcon className="h-6 sm:h-10 w-6 sm:w-10" />
                            </div>
                            <h1 className="text-3xl font-bold mb-2">
                                Interview In Progress
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400 mb-8 mx-auto whitespace-pre-line">
                                You are currently taking this interview.
                                <br /><br />
                                If this is a mistake, contact <b>support@ourteam.ai</b>.
                            </p>
                        </div>
                    </div>
                ) : isRequestedNewLink ? (
                    <div className="container mx-auto px-0 sm:px-4 pt-0 pb-2 sm:py-8 max-w-xl">
                        <div
                            className="bg-white dark:bg-gray-900 rounded-3xl p-8 shadow-2xl text-center w-full mx-auto"
                            style={{
                                boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
                            }}
                        >
                            <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-blue-100 text-blue-700 mx-auto mb-6">
                                <Link2 className="h-6 sm:h-10 w-6 sm:w-10" />
                            </div>
                            <h1 className="text-3xl font-bold mb-2">
                                New Interview Link Requested
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400 mb-8 mx-auto whitespace-pre-line">
                                Your request has been submitted. Once approved by our HR team, <br />  you will receive a new interview link via email.
                            </p>
                        </div>
                    </div>
                ) : (!isLoading && !isLoadingInterviewStatus && (!payloadToken || !payloadToken.user_id || !payloadToken.job_id || !payloadToken.expired_date)) ? (
                    <div className="container mx-auto px-0 sm:px-4 pt-0 pb-2 sm:py-8 max-w-xl">
                        <div
                            className="bg-white dark:bg-gray-900 rounded-3xl p-8 shadow-2xl text-center w-full mx-auto"
                            style={{
                                boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
                            }}
                        >
                            <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-red-100 text-red-700 mx-auto mb-6">
                                <VideoIcon className="h-6 sm:h-10 w-6 sm:w-10" />
                            </div>
                            <h1 className="text-3xl font-bold mb-2">
                                Invalid Interview Link
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400 mb-8 mx-auto whitespace-pre-line">
                                <>
                                    The interview link you used is invalid.
                                    <br />
                                    Please contact <b>support@ourteam.ai</b> for assistance.
                                </>
                            </p>
                        </div>
                    </div>
                ) : (!isLoading && !isLoadingInterviewStatus && payloadToken && payloadToken.expired_date && new Date(payloadToken.expired_date) < new Date()) ? (
                    <div className="container mx-auto px-0 sm:px-4 pt-0 pb-2 sm:py-8 max-w-xl">
                        <div
                            className="bg-white dark:bg-gray-900 rounded-3xl p-8 shadow-2xl text-center w-full mx-auto"
                            style={{
                                boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
                            }}
                        >
                            <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-red-100 text-red-700 mx-auto mb-6">
                                <Link2OffIcon className="h-6 sm:h-10 w-6 sm:w-10" />
                            </div>
                            <h1 className="text-3xl font-bold mb-2">
                                Interview Link Expired
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400 mb-8 mx-auto whitespace-pre-line">
                                Your interview link has expired.
                                <br />
                                Please contact <b>support@ourteam.ai</b> for assistance.
                            </p>
                            <Button
                                variant="default"
                                className="w-full sm:w-auto bg-blue-600 flex-1"
                                onClick={onRequestNewLink}
                                disabled={isRequestingNewLink}
                            >
                                {isRequestingNewLink ? "Requesting..." : "Request New Link"}
                            </Button>
                        </div>
                    </div>
                ) : (!isLoading && !isLoadingInterviewStatus) && (
                    <>
                        {[interviewStatusAborted, interviewStatusPartial].includes(currentInterviewStatus) ? (
                            <div className="container mx-auto px-0 sm:px-4 pt-0 pb-2 sm:py-8 max-w-xl">
                                <InterviewShortCard
                                    description={(
                                        <>
                                            We didn’t get enough answers to generate your full results.
                                            <br /><br />
                                            You can retake the interview or view the<br />partial insights we captured.
                                        </>
                                    )}
                                />
                            </div>
                        ) : (
                            <>
                                {interviewStage === "interview" && jobDetail && (
                                    <DailyProvider callObject={callObject}>
                                        <SpeechDetectionProvider
                                            volumeThreshold={30}
                                            silenceDelay={400}
                                            autoStart={true}
                                        >
                                            <VoiceInterview
                                                pipecatApiUrl={import.meta.env.VITE_PIPECAT_API_URL}
                                                agentName={import.meta.env.VITE_PIPECAT_AGENT_NAME}
                                                authToken={import.meta.env.VITE_PIPECAT_AUTH_TOKEN}
                                                interviewContext={INTERVIEW_CONFIG.conversationalContext}
                                                s3BucketName={import.meta.env.VITE_AWS_S3_BUCKET_NAME}
                                                s3BucketRegion={import.meta.env.VITE_AWS_S3_BUCKET_REGION}
                                                awsAssumeRoleArn={import.meta.env.VITE_AWS_ASSUME_ROLE_ARN}
                                                conversationName={INTERVIEW_CONFIG.conversationName}
                                                fastapiURL={import.meta.env.VITE_TALENT_AI_BE_BASE_URL}
                                                onCloseModal={handleInterviewComplete} // Use the new handler
                                                customGreeting={REPLICA_CONFIG.greeting}
                                                userId={userId}
                                                userName={getFirstName(cvData?.name as string) || ""}
                                                onReload={onReload}
                                                onInterviewStatusChange={(status: string) => {
                                                    setCurrentInterviewStatus(status);
                                                }}
                                                handleEndSession={onInterviewEnded}
                                            />
                                        </SpeechDetectionProvider>
                                    </DailyProvider>
                                )}
                            </>
                        )}
                        {(interviewStage === "complete") && (
                            <>
                                <Card>
                                    <CardHeader>
                                        <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <CheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                                        </div>
                                        <CardTitle className="text-center text-2xl">
                                            Interview Complete!
                                        </CardTitle>
                                        <CardDescription className="text-center">
                                            Thank you for completing your 20 minutes screening interview with <strong>ourteam AI.</strong>
                                            <br />
                                            We’ll now share your profile with top companies hiring for roles that match your skills.
                                        </CardDescription>
                                    </CardHeader>
                                    <CardFooter>
                                        <Button
                                            onClick={() => {
                                                window.location.href = "/dashboard";
                                            }}
                                            className="w-full group"
                                        >
                                            Continue to Dashboard
                                            <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                        </Button>
                                    </CardFooter>
                                </Card>
                            </>
                        )}
                    </>
                )}

                {![interviewStatusAborted, interviewStatusPartial].includes(currentInterviewStatus) && (
                    <SocialProofIndicator variant="companies" className="opacity-80 mt-10 mb-20 sm:mb-0" />
                )}
            </div>
        </BasicLayout >
    )
}